import { CrudRecord, CrudResourceConfig } from './types';
import { t } from '../i18n';
import {
  yesNo,
  boolFlags,
  relationIds,
  deploymentCategoryIds,
  normalizeItemAfterLoad,
  normalizePromotionAfterLoad,
  normalizeItemBeforeSave,
  normalizeItemOfType,
  normalizeSaleBeforeSave,
  normalizeAdjustmentBeforeSave,
  normalizeTransferBeforeSave,
  normalizePurchaseBeforeSave,
  normalizeQuotationBeforeSave,
  normalizeRecurringSaleBeforeSave,
  normalizeIncomeBeforeSave,
  normalizeExpenseBeforeSave,
  normalizePaymentBeforeSave,
  names,
  relationName,
  partyName,
  categoriesSummary,
  itemCategoryName,
  locationStockQuantity,
  itemStockSummary,
  countSummary,
  listSummary,
  comboPortionsSummary,
  comboCompositionCount,
  itemVariantSummary,
  yesNoValue,
  moneyValue,
  cashDifference,
  paymentStatusColumn,
  accountingDetailSections,
  paymentDetailSections,
  accountDetailSections,
  simpleDetailSections,
  partyDetailSections,
  orderDetailSections,
  adjustmentDetailSections,
  stockTransferDetailSections
} from './configHelpers';

export const referenceResources: CrudResourceConfig[] = [
  {
    resource: 'items',
    title: 'Items',
    singular: 'Item',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'category', label: 'Category', render: itemCategoryName },
      { key: 'brand', label: 'Brand', render: (row) => relationName(row.brand) },
      { key: 'unit', label: 'Unit', render: (row) => relationName(row.unit) },
      { key: 'price', label: 'Price', render: (row) => moneyValue(row.price) },
      { key: 'cost', label: 'Cost', render: (row) => moneyValue(row.cost) },
      { key: 'stock', label: 'Stock', render: locationStockQuantity },
      { key: 'hide_in_pos', label: 'POS', render: (row) => (row.hide_in_pos ? t('status.hidden') : t('status.visible')) },
      { key: 'hide_in_shop', label: 'Shop', render: (row) => (row.hide_in_shop ? t('status.hidden') : t('status.visible')) }
    ],
    detailSections: [
      {
        title: 'Identity',
        fields: [
          { key: 'code', label: 'Code' },
          { key: 'name', label: 'Name' },
          { key: 'type', label: 'Type' },
          { key: 'slug', label: 'Slug' },
          { key: 'sku', label: 'SKU' },
          { key: 'alt_name', label: 'Alternate Name' },
          { key: 'symbology', label: 'Symbology' }
        ]
      },
      {
        title: 'Pricing',
        fields: [
          { key: 'cost', label: 'Cost' },
          { key: 'price', label: 'Price' },
          { key: 'min_price', label: 'Minimum Price' },
          { key: 'max_price', label: 'Maximum Price' },
          { key: 'max_discount', label: 'Max Discount' },
          { key: 'tax_included', label: 'Tax Included', render: (row) => yesNoValue(row.tax_included) }
        ]
      },
      {
        title: 'Relations',
        fields: [
          { key: 'categories', label: 'Categories', render: (row) => names(row.categories) },
          { key: 'taxes', label: 'Taxes', render: (row) => names(row.taxes) },
          { key: 'brand', label: 'Brand', render: (row) => relationName(row.brand) },
          { key: 'unit', label: 'Unit', render: (row) => relationName(row.unit) },
          { key: 'modifiers', label: 'Modifiers', render: (row) => names(row.modifiers) }
        ]
      },
      {
        title: 'Location Stock',
        render: itemStockSummary
      },
      {
        title: 'Variants',
        render: itemVariantSummary
      },
      {
        title: 'Portions',
        render: (row) => listSummary(row, 'portions', 'No portions', ['sku', 'price', 'cost', 'quantity'])
      },
      {
        title: 'Serials',
        render: (row) => listSummary(row, 'serials', 'No serials', ['serial', 'number', 'sku', 'quantity'])
      },
      {
        title: 'Modifiers',
        render: (row) => listSummary(row, 'modifiers', 'No modifiers', ['price', 'cost', 'quantity'])
      },
      {
        title: 'Visibility',
        fields: [
          { key: 'is_stock', label: 'Track Stock', render: (row) => yesNoValue(row.is_stock) },
          { key: 'is_service', label: 'Service Item', render: (row) => yesNoValue(row.is_service) },
          { key: 'hide_in_pos', label: 'Hidden in POS', render: (row) => yesNoValue(row.hide_in_pos) },
          { key: 'hide_in_shop', label: 'Hidden in Shop', render: (row) => yesNoValue(row.hide_in_shop) },
          { key: 'changeable', label: 'Changeable Price', render: (row) => yesNoValue(row.changeable) },
          { key: 'has_variants', label: 'Has Variants', render: (row) => yesNoValue(row.has_variants) },
          { key: 'has_serials', label: 'Has Serials', render: (row) => yesNoValue(row.has_serials) }
        ]
      },
      {
        title: 'Advanced Structures',
        fields: [
          { key: 'stock', label: 'Stock Rows', render: (row) => countSummary(row, 'stock', 'stock row') },
          { key: 'variations', label: 'Variations', render: (row) => countSummary(row, 'variations', 'variation') },
          { key: 'variants', label: 'Variants', render: (row) => countSummary(row, 'variants', 'variant') },
          { key: 'portions', label: 'Portions', render: (row) => countSummary(row, 'portions', 'portion') },
          { key: 'serials', label: 'Serials', render: (row) => countSummary(row, 'serials', 'serial') },
          { key: 'modifiers', label: 'Modifiers', render: (row) => countSummary(row, 'modifiers', 'modifier') }
        ]
      },
      {
        title: 'Notes',
        fields: [
          { key: 'details', label: 'Details' },
          { key: 'summary', label: 'Summary' }
        ]
      }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true, section: 'Identity' },
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      {
        name: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        defaultValue: 'standard',
        options: ['standard', 'service', 'recipe', 'combo', 'download'].map((value) => ({ label: value, value })),
        section: 'Identity'
      },
      { name: 'category_id', label: 'Category', type: 'select', required: true, optionsEndpoint: 'categories/search', section: 'Relations' },
      { name: 'brand_id', label: 'Brand', type: 'select', optionsEndpoint: 'brands/search', section: 'Relations' },
      { name: 'unit_id', label: 'Unit', type: 'select', optionsEndpoint: 'units/search', section: 'Relations' },
      { name: 'taxes', label: 'Taxes', type: 'multiselect', optionsEndpoint: 'taxes/search', section: 'Relations' },
      { name: 'modifiers', label: 'Modifiers', type: 'multiselect', optionsEndpoint: 'modifiers/search', section: 'Relations', helperText: 'Attach existing modifiers (e.g. Extra shot). Modifier options are managed separately.' },
      { name: 'symbology', label: 'Symbology', type: 'select', required: true, defaultValue: 'code128', options: ['code128', 'ean13', 'upca', 'qrcode'].map((value) => ({ label: value, value })), section: 'Identity' },
      { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'price', label: 'Price', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'sku', label: 'SKU', section: 'Identity' },
      { name: 'alt_name', label: 'Alternate Name', section: 'Identity' },
      { name: 'rack', label: 'Rack', section: 'Inventory' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' },
      { name: 'summary', label: 'Summary', type: 'textarea', section: 'Notes' },
      { name: 'is_stock', label: 'Track stock', type: 'checkbox', defaultValue: true, section: 'Visibility' },
      { name: 'tax_included', label: 'Tax included', type: 'checkbox', section: 'Pricing' },
      { name: 'hide_in_pos', label: 'Hide in POS', type: 'checkbox', section: 'Visibility' },
      { name: 'hide_in_shop', label: 'Hide in shop', type: 'checkbox', section: 'Visibility' },
      { name: 'has_variants', label: 'Has variants', type: 'checkbox', section: 'Visibility', helperText: 'Enable when this item comes in variants (e.g. size/color). Variations are created with zero stock.' },
      { name: 'has_serials', label: 'Has serials', type: 'checkbox', section: 'Visibility', helperText: 'Serial numbers are added via the purchase flow, not this form.' },
      {
        name: 'portions',
        label: 'Portions',
        type: 'subform',
        section: 'Pricing',
        helperText: 'Price/size overrides (e.g. Small/Medium/Large). Stock-neutral — portions carry no inventory.',
        subformFields: [
          { name: 'sku', label: 'SKU', required: true },
          { name: 'name', label: 'Name', required: true },
          { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0 },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 }
        ]
      },
      {
        name: 'variations',
        label: 'Variations',
        type: 'subform',
        section: 'Pricing',
        helperText: 'Variant combinations (e.g. Size:Red). Always created with ZERO stock — manage stock via stock-adjustment or purchase flows. Axis:value pairs in Meta (comma-separated, e.g. Size:M,Color:Red).',
        subformFields: [
          { name: 'sku', label: 'SKU', required: true },
          { name: 'code', label: 'Code' },
          { name: 'meta', label: 'Meta (Axis:Value)', required: true, helperText: 'e.g. Size:M' },
          { name: 'cost', label: 'Cost', type: 'number', defaultValue: 0 },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 }
        ]
      }
    ],
    normalizeAfterLoad: normalizeItemAfterLoad,
    normalizeBeforeSave: normalizeItemBeforeSave,
    sectionMeta: {
      Identity: { description: 'The core identifier — code, name, and product type.' },
      Relations: { description: 'Classify this product within your catalog.' },
      Pricing: { description: 'Set the cost, selling price, and tax behavior. Variations and portions override these defaults.' },
      Inventory: { description: 'Where this product is physically stored.' },
      Visibility: { description: 'Control where this product appears and how stock is tracked.' },
      Notes: { description: 'Additional details for staff and customers.' }
    },
    workspaceSummary: (form) => {
      const cost = Number(form.cost ?? 0);
      const price = Number(form.price ?? 0);
      const margin = price - cost;
      const marginPct = price > 0 ? Math.round((margin / price) * 100) : 0;
      const variations = Array.isArray(form.variations) ? form.variations.length : 0;
      const portions = Array.isArray(form.portions) ? form.portions.length : 0;
      return [
        { label: 'Margin', value: margin.toLocaleString(), tone: margin > 0 ? 'success' : margin < 0 ? 'error' : 'default' },
        { label: 'Margin %', value: `${marginPct}%`, tone: marginPct > 30 ? 'success' : marginPct < 0 ? 'error' : 'default' },
        { label: 'Stock tracking', value: form.is_stock ? 'Enabled' : 'Disabled', tone: form.is_stock ? 'success' : 'warning' },
        ...(variations > 0 ? [{ label: 'Variations', value: String(variations), tone: 'default' as const }] : []),
        ...(portions > 0 ? [{ label: 'Portions', value: String(portions), tone: 'default' as const }] : [])
      ];
    }
  },
  // --- Item sub-types (services, recipes, combos) ---------------------------
  // These share the same backend resource ('items') but filter by type via
  // listFilter and force the type on save. The legacy app presents them as
  // separate menu entries with dedicated forms; here they reuse the same
  // config shape with a type override.
  {
    resource: 'items-services',
    title: 'Services',
    singular: 'Service',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    apiResource: 'items',
    listFilter: { type: 'service' },
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category', render: itemCategoryName },
      { key: 'price', label: 'Price', render: (row) => moneyValue(row.price) },
      { key: 'cost', label: 'Cost', render: (row) => moneyValue(row.cost) },
      { key: 'hide_in_pos', label: 'POS', render: (row) => (row.hide_in_pos ? t('status.hidden') : t('status.visible')) }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true, section: 'Identity' },
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'symbology', label: 'Symbology', type: 'select', required: true, defaultValue: 'code128', options: ['code128', 'ean13', 'upca', 'qrcode'].map((value) => ({ label: value, value })), section: 'Identity' },
      { name: 'sku', label: 'SKU', section: 'Identity' },
      { name: 'category_id', label: 'Category', type: 'select', required: true, optionsEndpoint: 'categories/search', section: 'Relations' },
      { name: 'brand_id', label: 'Brand', type: 'select', optionsEndpoint: 'brands/search', section: 'Relations' },
      { name: 'unit_id', label: 'Unit', type: 'select', optionsEndpoint: 'units/search', section: 'Relations' },
      { name: 'taxes', label: 'Taxes', type: 'multiselect', optionsEndpoint: 'taxes/search', section: 'Relations' },
      { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'price', label: 'Price', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'tax_included', label: 'Tax included', type: 'checkbox', section: 'Pricing' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' },
      { name: 'summary', label: 'Summary', type: 'textarea', section: 'Notes' },
      { name: 'hide_in_pos', label: 'Hide in POS', type: 'checkbox', section: 'Visibility' },
      { name: 'hide_in_shop', label: 'Hide in shop', type: 'checkbox', section: 'Visibility' }
    ],
    detailSections: [
      {
        title: 'Identity',
        fields: [
          { key: 'code', label: 'Code' },
          { key: 'name', label: 'Name' },
          { key: 'type', label: 'Type' },
          { key: 'slug', label: 'Slug' },
          { key: 'sku', label: 'SKU' },
          { key: 'symbology', label: 'Symbology' }
        ]
      },
      {
        title: 'Pricing',
        fields: [
          { key: 'cost', label: 'Cost', render: (row: CrudRecord) => moneyValue(row.cost) },
          { key: 'price', label: 'Price', render: (row: CrudRecord) => moneyValue(row.price) },
          { key: 'tax_included', label: 'Tax Included' }
        ]
      },
      {
        title: 'Relations',
        fields: [
          { key: 'categories', label: 'Categories', render: categoriesSummary },
          { key: 'brand', label: 'Brand', render: (row: CrudRecord) => relationName(row.brand) },
          { key: 'unit', label: 'Unit', render: (row: CrudRecord) => relationName(row.unit) },
          { key: 'taxes', label: 'Taxes', render: (row: CrudRecord) => countSummary(row, 'taxes', 'tax') }
        ]
      },
      { title: 'Notes', fields: [{ key: 'details', label: 'Details' }, { key: 'summary', label: 'Summary' }] }
    ],
    normalizeBeforeSave: (payload) => normalizeItemOfType(payload, 'service')
  },
  {
    resource: 'items-recipes',
    title: 'Recipes',
    singular: 'Recipe',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    apiResource: 'items',
    listFilter: { type: 'recipe' },
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category', render: itemCategoryName },
      { key: 'price', label: 'Price', render: (row) => moneyValue(row.price) },
      { key: 'cost', label: 'Cost', render: (row) => moneyValue(row.cost) },
      { key: 'portions', label: 'Combo Items', render: comboCompositionCount }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true, section: 'Identity' },
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'symbology', label: 'Symbology', type: 'select', required: true, defaultValue: 'code128', options: ['code128', 'ean13', 'upca', 'qrcode'].map((value) => ({ label: value, value })), section: 'Identity' },
      { name: 'sku', label: 'SKU', section: 'Identity' },
      { name: 'category_id', label: 'Category', type: 'select', required: true, optionsEndpoint: 'categories/search', section: 'Relations' },
      { name: 'unit_id', label: 'Unit', type: 'select', optionsEndpoint: 'units/search', section: 'Relations' },
      { name: 'taxes', label: 'Taxes', type: 'multiselect', optionsEndpoint: 'taxes/search', section: 'Relations' },
      { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'price', label: 'Price', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'tax_included', label: 'Tax included', type: 'checkbox', section: 'Pricing' },
      {
        name: 'portions',
        label: 'Portions',
        type: 'subform',
        section: 'Pricing',
        helperText: 'Price/size overrides (e.g. Small/Medium/Large). Stock-neutral.',
        subformFields: [
          { name: 'sku', label: 'SKU', required: true },
          { name: 'name', label: 'Name', required: true },
          { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0 },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 }
        ]
      },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' },
      { name: 'hide_in_pos', label: 'Hide in POS', type: 'checkbox', section: 'Visibility' }
    ],
    detailSections: [
      {
        title: 'Identity',
        fields: [
          { key: 'code', label: 'Code' },
          { key: 'name', label: 'Name' },
          { key: 'type', label: 'Type' },
          { key: 'slug', label: 'Slug' },
          { key: 'sku', label: 'SKU' },
          { key: 'symbology', label: 'Symbology' }
        ]
      },
      {
        title: 'Pricing',
        fields: [
          { key: 'cost', label: 'Cost', render: (row: CrudRecord) => moneyValue(row.cost) },
          { key: 'price', label: 'Price', render: (row: CrudRecord) => moneyValue(row.price) },
          { key: 'tax_included', label: 'Tax Included' }
        ]
      },
      {
        title: 'Relations',
        fields: [
          { key: 'categories', label: 'Categories', render: categoriesSummary },
          { key: 'unit', label: 'Unit', render: (row: CrudRecord) => relationName(row.unit) },
          { key: 'taxes', label: 'Taxes', render: (row: CrudRecord) => countSummary(row, 'taxes', 'tax') }
        ]
      },
      { title: 'Combo Items', render: comboPortionsSummary },
      { title: 'Notes', fields: [{ key: 'details', label: 'Details' }] }
    ],
    normalizeBeforeSave: (payload) => normalizeItemOfType(payload, 'recipe')
  },
  {
    resource: 'items-combos',
    title: 'Combos',
    singular: 'Combo',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    apiResource: 'items',
    listFilter: { type: 'combo' },
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category', render: itemCategoryName },
      { key: 'price', label: 'Price', render: (row) => moneyValue(row.price) },
      { key: 'cost', label: 'Cost', render: (row) => moneyValue(row.cost) },
      { key: 'portions', label: 'Portions', render: (row) => countSummary(row, 'portions', 'portion') }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true, section: 'Identity' },
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'symbology', label: 'Symbology', type: 'select', required: true, defaultValue: 'code128', options: ['code128', 'ean13', 'upca', 'qrcode'].map((value) => ({ label: value, value })), section: 'Identity' },
      { name: 'sku', label: 'SKU', section: 'Identity' },
      { name: 'category_id', label: 'Category', type: 'select', required: true, optionsEndpoint: 'categories/search', section: 'Relations' },
      { name: 'unit_id', label: 'Unit', type: 'select', optionsEndpoint: 'units/search', section: 'Relations' },
      { name: 'taxes', label: 'Taxes', type: 'multiselect', optionsEndpoint: 'taxes/search', section: 'Relations' },
      { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'price', label: 'Price', type: 'number', required: true, defaultValue: 0, section: 'Pricing' },
      { name: 'tax_included', label: 'Tax included', type: 'checkbox', section: 'Pricing' },
      {
        name: 'portions',
        label: 'Portions',
        type: 'subform',
        section: 'Pricing',
        helperText: 'Component choices for this combo (e.g. choose a drink, choose a side).',
        subformFields: [
          { name: 'sku', label: 'SKU', required: true },
          { name: 'name', label: 'Name', required: true },
          { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0 },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 }
        ]
      },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' },
      { name: 'hide_in_pos', label: 'Hide in POS', type: 'checkbox', section: 'Visibility' }
    ],
    detailSections: [
      {
        title: 'Identity',
        fields: [
          { key: 'code', label: 'Code' },
          { key: 'name', label: 'Name' },
          { key: 'type', label: 'Type' },
          { key: 'slug', label: 'Slug' },
          { key: 'sku', label: 'SKU' },
          { key: 'symbology', label: 'Symbology' }
        ]
      },
      {
        title: 'Pricing',
        fields: [
          { key: 'cost', label: 'Cost', render: (row: CrudRecord) => moneyValue(row.cost) },
          { key: 'price', label: 'Price', render: (row: CrudRecord) => moneyValue(row.price) },
          { key: 'tax_included', label: 'Tax Included' }
        ]
      },
      {
        title: 'Relations',
        fields: [
          { key: 'categories', label: 'Categories', render: categoriesSummary },
          { key: 'unit', label: 'Unit', render: (row: CrudRecord) => relationName(row.unit) },
          { key: 'taxes', label: 'Taxes', render: (row: CrudRecord) => countSummary(row, 'taxes', 'tax') }
        ]
      },
      { title: 'Portions', render: (row: CrudRecord) => listSummary(row, 'portions', 'No portions', ['sku', 'price', 'cost', 'quantity']) },
      { title: 'Notes', fields: [{ key: 'details', label: 'Details' }] }
    ],
    normalizeBeforeSave: (payload) => normalizeItemOfType(payload, 'combo')
  },
  {
    resource: 'customers',
    title: 'Customers',
    singular: 'Customer',
    orderBy: 'name asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'company', label: 'Company' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'journal', label: 'Balance', render: (row) => moneyValue((row.journal as { balance?: unknown } | undefined)?.balance) }
    ],
    detailSections: partyDetailSections,
    fields: [
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'company', label: 'Company', section: 'Identity' },
      { name: 'email', label: 'Email', section: 'Identity' },
      { name: 'phone', label: 'Phone', section: 'Identity' },
      { name: 'address', label: 'Address', type: 'textarea', section: 'Address' },
      { name: 'city', label: 'City', section: 'Address' },
      { name: 'postal_code', label: 'Postal Code', section: 'Address' },
      { name: 'opening_balance', label: 'Opening Balance', type: 'number', defaultValue: 0, section: 'Credit' },
      { name: 'due_limit', label: 'Due Limit', type: 'number', section: 'Credit' },
      { name: 'max_due_amount', label: 'Max Due Amount', type: 'number', section: 'Credit' }
    ]
  },
  {
    resource: 'suppliers',
    title: 'Suppliers',
    singular: 'Supplier',
    orderBy: 'name asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'company', label: 'Company' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'journal', label: 'Balance', render: (row) => moneyValue((row.journal as { balance?: unknown } | undefined)?.balance) }
    ],
    detailSections: partyDetailSections,
    fields: [
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'company', label: 'Company', section: 'Identity' },
      { name: 'email', label: 'Email', section: 'Identity' },
      { name: 'phone', label: 'Phone', section: 'Identity' },
      { name: 'address', label: 'Address', type: 'textarea', section: 'Address' },
      { name: 'city', label: 'City', section: 'Address' },
      { name: 'postal_code', label: 'Postal Code', section: 'Address' },
      { name: 'opening_balance', label: 'Opening Balance', type: 'number', defaultValue: 0, section: 'Credit' },
      { name: 'due_limit', label: 'Due Limit', type: 'number', section: 'Credit' },
      { name: 'max_due_amount', label: 'Max Due Amount', type: 'number', section: 'Credit' }
    ]
  },
  {
    resource: 'purchases',
    title: 'Purchases',
    singular: 'Purchase',
    orderBy: 'date desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'supplier', label: 'Supplier', render: (row) => String((row.supplier as { name?: string; company?: string } | undefined)?.name ?? (row.supplier as { company?: string } | undefined)?.company ?? '-') },
      { key: 'grand_total', label: 'Grand Total', render: (row) => moneyValue(row.grand_total) },
      { key: 'paid', label: 'Paid', render: (row) => moneyValue(row.paid) }
    ],
    detailSections: orderDetailSections('supplier'),
    fields: [
      { name: 'type', label: 'Type', type: 'select', required: true, defaultValue: 'purchase', options: ['purchase', 'return'].map((value) => ({ label: value, value })), section: 'Details', helperText: t('order.finalizeInLegacyHelper') },
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'supplier_id', label: 'Supplier', type: 'select', required: true, optionsEndpoint: 'suppliers/search', section: 'Details' },
      {
        name: 'items',
        label: 'Line Items',
        type: 'subform',
        section: 'Items',
        helperText: t('order.noStockDeductedHelper'),
        subformFields: [
          { name: 'item_id', label: 'Item ID', required: true },
          { name: 'code', label: 'Code' },
          { name: 'name', label: 'Name' },
          { name: 'cost', label: 'Cost', type: 'number', defaultValue: 0 },
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 }
        ]
      }
    ],
    normalizeBeforeSave: normalizePurchaseBeforeSave
  },
  {
    resource: 'sales',
    title: 'Sales',
    singular: 'Sale',
    orderBy: 'date desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    canFinalize: true,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'customer', label: 'Customer', render: (row) => String((row.customer as { name?: string; company?: string } | undefined)?.name ?? (row.customer as { company?: string } | undefined)?.company ?? '-') },
      { key: 'location', label: 'Location', render: (row) => relationName(row.location) || String(row.location_id ?? '-') },
      { key: 'register', label: 'Cash Register', render: (row) => relationName(row.register) || String(row.register_id ?? '-') },
      { key: 'grand_total', label: 'Grand Total', render: (row) => moneyValue(row.grand_total) },
      { key: 'payment_gateway', label: 'Payment', render: (row) => {
        const gw = row.payment_gateway ?? row.gateway;
        if (gw) return String(gw);
        return row.draft ? '—' : (row.paid ? 'Paid' : 'Unpaid');
      } },
      { key: 'paid', label: 'Status', render: (row) => paymentStatusColumn(row) }
    ],
    detailSections: orderDetailSections('customer'),
    fields: [
      { name: 'type', label: 'Type', type: 'select', required: true, defaultValue: 'sale', options: ['sale', 'return'].map((value) => ({ label: value, value })), section: 'Details', helperText: t('order.finalizeInLegacyHelper') },
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'customer_id', label: 'Customer', type: 'select', required: true, optionsEndpoint: 'customers/search', section: 'Details' },
      {
        name: 'items',
        label: 'Line Items',
        type: 'subform',
        section: 'Items',
        helperText: t('order.noStockDeductedHelper'),
        subformFields: [
          { name: 'item_id', label: 'Item ID', required: true },
          { name: 'code', label: 'Code' },
          { name: 'name', label: 'Name' },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 },
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 }
        ]
      }
    ],
    normalizeBeforeSave: normalizeSaleBeforeSave
  },
  {
    // Orders are intentionally read-only. They are transient POS entities —
    // temporary carts created by the cash register that get consumed when a
    // sale is finalized. The backend store() requires an open register session
    // and returns empty without one; there is no update() method at all.
    // Orders cannot be created or edited from the admin panel.
    resource: 'orders',
    title: 'Orders',
    singular: 'Order',
    orderBy: 'date desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    readOnly: true,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'customer', label: 'Customer', render: (row) => String((row.customer as { name?: string; company?: string } | undefined)?.name ?? '-') },
      { key: 'total_items', label: 'Items' },
      { key: 'grand_total', label: 'Grand Total', render: (row) => moneyValue(row.grand_total) }
    ],
    detailSections: orderDetailSections('customer'),
    fields: []
  },
  {
    // Accounts: no observer, but model boot creates a journal + posts an
    // opening_balance credit on create. Guardrail: opening_balance forced to 0
    // so React-created accounts have an empty, deletable journal.
    resource: 'accounts',
    title: 'Accounts',
    singular: 'Account',
    orderBy: 'name asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'reference', label: 'Reference' },
      { key: 'type', label: 'Type' },
      { key: 'opening_balance', label: 'Opening Balance', render: (row) => moneyValue(row.opening_balance) },
      { key: 'journal', label: 'Journal Balance', render: (row) => moneyValue((row.journal as { balance?: unknown } | undefined)?.balance) }
    ],
    detailSections: accountDetailSections,
    fields: [
      { name: 'name', label: 'Account Name', required: true, section: 'Identity' },
      { name: 'type', label: 'Type', type: 'select', required: true, defaultValue: 'cash', options: ['cash', 'bank', 'card', 'cheque', 'other'].map((value) => ({ label: value, value })), section: 'Identity' },
      { name: 'reference', label: 'Reference', section: 'Details' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ],
    normalizeBeforeSave: (payload) => ({
      name: payload.name,
      type: payload.type || 'cash',
      reference: payload.reference ?? '',
      details: payload.details ?? '',
      // HARD GUARDRAIL: opening_balance forced to 0 to prevent posting an
      // accounting entry from the React form. Set opening balances in the
      // legacy admin or via journal transactions.
      opening_balance: 0
    })
  },
  {
    // Payments: draft-gated (received=0). Observer skips journal postings when
    // received is false. Accounting deferred to the review() action.
    resource: 'payments',
    title: 'Payments',
    singular: 'Payment',
    orderBy: 'created_at desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'created_at', label: 'Created' },
      { key: 'reference', label: 'Reference' },
      { key: 'payable', label: 'Payer / Payee', render: partyName },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
      { key: 'gateway', label: 'Gateway' },
      { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) },
      { key: 'received', label: 'Received', render: (row) => yesNoValue(row.received) }
    ],
    detailSections: paymentDetailSections,
    fields: [
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'amount', label: 'Amount', type: 'number', required: true, defaultValue: 0, section: 'Details', helperText: 'Draft — not posted to the ledger until received.' },
      { name: 'account_id', label: 'Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Details' },
      {
        name: 'gateway',
        label: 'Gateway',
        type: 'select',
        required: true,
        defaultValue: 'bank_transfer',
        options: ['bank_transfer', 'cheque', 'card', 'other'].map((value) => ({ label: value, value })),
        section: 'Details',
        helperText: 'Use bank_transfer to keep this as a draft (cash/other auto-receive).'
      },
      { name: 'reference', label: 'Reference', section: 'Details' }
    ],
    normalizeBeforeSave: normalizePaymentBeforeSave
  },
  {
    // Incomes: draft-gated (approved=0). Observer skips journal credit when
    // approved is false. Journal posted when approved via the legacy admin.
    resource: 'incomes',
    title: 'Incomes',
    singular: 'Income',
    orderBy: 'date desc',
    searchField: 'title',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
      { key: 'categories', label: 'Categories', render: categoriesSummary },
      { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) }
    ],
    detailSections: accountingDetailSections,
    fields: [
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'title', label: 'Title', required: true, section: 'Details' },
      { name: 'amount', label: 'Amount', type: 'number', required: true, defaultValue: 0, section: 'Details', helperText: 'Draft — not posted to the ledger until approved.' },
      { name: 'account_id', label: 'Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Details' },
      { name: 'reference', label: 'Reference', section: 'Details' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ],
    normalizeBeforeSave: normalizeIncomeBeforeSave
  },
  {
    // Expenses: draft-gated (approved=0). Observer skips journal debit when
    // approved is false. Journal posted on approve() action.
    resource: 'expenses',
    title: 'Expenses',
    singular: 'Expense',
    orderBy: 'date desc',
    searchField: 'title',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'title', label: 'Title' },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
      { key: 'categories', label: 'Categories', render: categoriesSummary },
      { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) },
      { key: 'approved', label: 'Approved', render: (row) => yesNoValue(row.approved) }
    ],
    detailSections: accountingDetailSections,
    fields: [
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'title', label: 'Title', required: true, section: 'Details' },
      { name: 'amount', label: 'Amount', type: 'number', required: true, defaultValue: 0, section: 'Details', helperText: 'Draft — not posted to the ledger until approved.' },
      { name: 'account_id', label: 'Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Details' },
      { name: 'reference', label: 'Reference', section: 'Details' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ],
    normalizeBeforeSave: normalizeExpenseBeforeSave
  },
  {
    // Stock Adjustments: draft-gated (draft=1). The AdjustmentEventListner
    // skips the stock mutation when draft is truthy. Finalize flips draft→0
    // which triggers the addition/damage stock movement.
    resource: 'adjustments',
    title: 'Stock Adjustments',
    singular: 'Stock Adjustment',
    orderBy: 'created_at desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'stock-adjustments',
    canFinalize: true,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'type', label: 'Type' },
      { key: 'draft', label: 'Status', render: (row) => (row.draft ? t('status.draft') : t('status.posted')) },
      { key: 'user', label: 'User', render: (row) => relationName(row.user) },
      { key: 'items', label: 'Items', render: (row) => countSummary(row, 'items', 'item') },
      { key: 'details', label: 'Details' }
    ],
    detailSections: adjustmentDetailSections,
    fields: [
      { name: 'type', label: 'Type', type: 'select', required: true, defaultValue: 'addition', options: ['addition', 'damage', 'subtraction'].map((value) => ({ label: value, value })), section: 'Details', helperText: 'Addition adds stock; damage/subtraction removes it.' },
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'reference', label: 'Reference', section: 'Details', helperText: 'Leave blank to generate automatically.' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' },
      {
        name: 'items',
        label: 'Line Items',
        type: 'subform',
        required: true,
        section: 'Items',
        helperText: 'Draft — no stock is moved until finalized.',
        itemSearchEndpoint: 'items_wp/search?type=standard,service',
        itemResolveEndpoint: 'purchase_order_items',
        itemSearchPlaceholder: t('order.searchOrScanBarcode'),
        subformFields: [
          { name: 'item_id', label: 'Item ID', required: true },
          { name: 'code', label: 'Code', required: true },
          { name: 'name', label: 'Name', required: true },
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 },
          { name: 'cost', label: 'Cost', type: 'number', required: true, defaultValue: 0 }
        ]
      }
    ],
    normalizeBeforeSave: normalizeAdjustmentBeforeSave
  },
  {
    // Stock Transfers: pending-gated (status='pending'). The SyncItemLocationStock
    // listener dispatches by status — 'pending' has no method and falls to __call
    // which logs but never mutates stock. Finalize flips status→'transferred'
    // which moves stock from the source to the destination location.
    resource: 'transfers/stock',
    title: 'Stock Transfers',
    singular: 'Stock Transfer',
    orderBy: 'created_at desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'stock-transfers',
    canFinalize: true,
    draftField: 'status',
    draftValue: 'pending',
    finalizeField: 'status',
    finalizeValue: 'transferred',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'status', label: 'Status' },
      { key: 'from', label: 'From', render: (row) => relationName(row.from_location ?? row.fromLocation ?? row.from) },
      { key: 'to', label: 'To', render: (row) => relationName(row.to_location ?? row.toLocation ?? row.to) },
      { key: 'user', label: 'User', render: (row) => relationName(row.user) }
    ],
    detailSections: stockTransferDetailSections,
    fields: [
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Route' },
      { name: 'from', label: 'From Location', type: 'select', required: true, optionsEndpoint: 'locations/search', section: 'Route' },
      { name: 'to', label: 'To Location', type: 'select', required: true, optionsEndpoint: 'locations/search', section: 'Route', helperText: 'Must be different from the source location.' },
      { name: 'reference', label: 'Reference', section: 'Route' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' },
      {
        name: 'items',
        label: 'Line Items',
        type: 'subform',
        required: true,
        section: 'Items',
        helperText: 'Pending — no stock moves until finalized.',
        subformFields: [
          { name: 'item_id', label: 'Item ID', required: true },
          { name: 'code', label: 'Code', required: true },
          { name: 'name', label: 'Name', required: true },
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 }
        ]
      }
    ],
    normalizeBeforeSave: normalizeTransferBeforeSave
  },
  {
    resource: 'transfers/asset',
    title: 'Asset Transfers',
    singular: 'Asset Transfer',
    orderBy: 'created_at desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'asset-transfers',
    columns: [
      { key: 'created_at', label: 'Created' },
      { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) },
      { key: 'from_account', label: 'From', render: (row) => relationName(row.from_account ?? row.from) },
      { key: 'to_account', label: 'To', render: (row) => relationName(row.to_account ?? row.to) },
      { key: 'user', label: 'User', render: (row) => relationName(row.user) }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'from', label: 'From Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Transfer', helperText: 'The account to transfer from.' },
      { name: 'to', label: 'To Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Transfer', helperText: 'The account to transfer to. Must be different from the source.' },
      { name: 'amount', label: 'Amount', type: 'number', required: true, defaultValue: 0, section: 'Transfer' },
      { name: 'details', label: 'Notes', type: 'textarea', section: 'Notes' }
    ]
  },
  {
    resource: 'reports/registers',
    title: 'Register Records',
    singular: 'Register Record',
    orderBy: 'created_at desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    readOnly: true,
    permissionResource: 'registers',
    columns: [
      { key: 'created_at', label: 'Opened' },
      { key: 'closed_at', label: 'Closed' },
      { key: 'closedByUser', label: 'Closed By', render: (row) => relationName(row.closedByUser ?? row.closed_by) },
      { key: 'register', label: 'Register', render: (row) => relationName(row.register) },
      { key: 'register_imei', label: 'IMEI', render: (row) => String((row.register as Record<string, unknown> | undefined)?.imei ?? '-') },
      { key: 'location', label: 'Location', render: (row) => relationName(row.location) },
      { key: 'user', label: 'User', render: (row) => relationName(row.user) },
      { key: 'cash_in_hand', label: 'Opening Cash', render: (row) => moneyValue(row.cash_in_hand) },
      { key: 'sales_sum_grand_total', label: 'Closing Amount', render: (row) => moneyValue(row.sales_sum_grand_total) },
      {
        key: 'cash_difference',
        label: 'Cash Difference',
        render: (row) => {
          const difference = cashDifference(row);
          return difference == null ? '-' : `${difference > 0 ? '+' : ''}${moneyValue(difference)}`;
        },
        tone: (row) => {
          const difference = cashDifference(row);
          if (difference == null) return undefined;
          if (Math.abs(difference) < 0.01) return 'success';
          return Math.abs(difference) >= 20 ? 'error' : 'warning';
        }
      }
    ],
    fields: []
  },
  {
    resource: 'users',
    title: 'Users',
    singular: 'User',
    orderBy: 'name asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'username', label: 'Username' },
      { key: 'email', label: 'Email' },
      { key: 'cashier_no', label: 'Cashier No' },
      { key: 'location', label: 'Location', render: (row) => relationName(row.location) },
      { key: 'roles', label: 'Roles', render: (row) => names(row.roles) }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'name', label: 'Full Name', required: true, section: 'Identity' },
      { name: 'username', label: 'Username', required: true, section: 'Identity', helperText: 'Used for web admin login.' },
      { name: 'email', label: 'Email', type: 'text', section: 'Contact' },
      { name: 'phone', label: 'Phone', type: 'text', section: 'Contact' },
      { name: 'roles', label: 'Roles', type: 'multiselect', optionsEndpoint: 'roles/search', section: 'Access', helperText: 'Controls what this user can do.' },
      { name: 'active', label: 'Active', type: 'checkbox', defaultValue: true, section: 'Status' },
      { name: 'employee', label: 'Staff member', type: 'checkbox', defaultValue: true, section: 'Status' },
      // Cashier / POS access — matches legacy: cashier checkbox, cashier_no, cashier_password with eye toggle
      { name: 'cashier', label: 'Cashier (POS access)', type: 'checkbox', defaultValue: false, section: 'Cashier', helperText: 'Enable to allow this user to log in on the POS cash register.' },
      { name: 'cashier_no', label: 'Cashier Number', section: 'Cashier', readOnly: true, helperText: 'Auto-assigned. Cannot be changed after creation.' },
      { name: 'cashier_password', label: 'Cashier PIN', type: 'password', section: 'Cashier', helperText: 'Numeric PIN for the cash register. Leave blank to keep current.' },
      { name: 'cashier_card_no', label: 'Cashier Card Number', type: 'text', section: 'Cashier', helperText: 'Card number used as the fast POS login key.', qrScan: true },
      // Admin password — matches legacy: required on create, toggle-gated on edit, with confirmation
      { name: 'password', label: 'New Password', type: 'password', section: 'Security', helperText: 'For web admin login. Leave blank on edit to keep current.' },
      { name: 'password_confirmation', label: 'Confirm Password', type: 'password', section: 'Security' }
    ],
    normalizeAfterLoad: (record) => ({
      ...record,
      roles: Array.isArray(record.roles)
        ? record.roles.map((role) => {
            if (role && typeof role === 'object') {
              const value = role as Record<string, unknown>;
              return String(value.name ?? value.label ?? value.value ?? value.id ?? '');
            }
            return String(role);
          }).filter(Boolean)
        : []
    }),
    normalizeBeforeSave: (payload) => {
      // Extract role names from the multiselect value. The Combobox returns
      // objects {id, name} or {value, label}. Spatie's syncRoles expects
      // an array of role name strings (e.g. ["super", "cashier"]).
      const rawRoles = Array.isArray(payload.roles) ? payload.roles : [];
      const roleNames = rawRoles.map((r: unknown) => {
        if (typeof r === 'string') return r;
        if (r && typeof r === 'object') {
          const obj = r as Record<string, unknown>;
          return String(obj.name ?? obj.label ?? obj.value ?? obj.id);
        }
        return String(r);
      });

      const result: Record<string, unknown> = {
        name: payload.name,
        username: payload.username,
        email: payload.email ?? '',
        phone: payload.phone ?? '',
        roles: roleNames,
        active: payload.active ? 1 : 0,
        employee: payload.employee ? 1 : 0,
        cashier: payload.cashier ? 1 : 0,
        cashier_no: payload.cashier_no ?? '',
        cashier_card_no: payload.cashier_card_no ?? '',
      };
      // Cashier password — optional, no confirmation (matches legacy)
      const cashierPw = String(payload.cashier_password ?? '');
      if (cashierPw) {
        result.cashier_password = cashierPw;
      }
      // Admin password — only send if non-empty, must match confirmation
      const adminPw = String(payload.password ?? '');
      const adminPwConfirm = String(payload.password_confirmation ?? '');
      if (adminPw) {
        if (adminPw !== adminPwConfirm) {
          throw new Error('Password and Confirm Password do not match.');
        }
        result.password = adminPw;
        result.password_confirmation = adminPwConfirm;
      }
      return result;
    }
  },
  {
    resource: 'roles',
    title: 'Roles',
    singular: 'Role',
    orderBy: 'name asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'guard_name', label: 'Guard' },
      { key: 'created_at', label: 'Created' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'name', label: 'Role Name', required: true, helperText: 'Lowercase, no spaces. Permissions are assigned separately in the legacy admin.' }
    ]
  },
  {
    resource: 'locations',
    title: 'Locations',
    singular: 'Location',
    orderBy: 'name asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'code', label: 'Code' },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
      { key: 'priceGroup', label: 'Price Group', render: (row) => relationName(row.priceGroup) },
      { key: 'registers', label: 'Registers', render: (row) => countSummary(row, 'registers', 'register') }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'name', label: 'Location Name', required: true, section: 'Identity' },
      { name: 'phone', label: 'Phone', required: true, section: 'Contact' },
      { name: 'email', label: 'Email', required: true, section: 'Contact' },
      { name: 'address', label: 'Address', required: true, section: 'Address' },
      { name: 'city', label: 'City', section: 'Address' },
      { name: 'state', label: 'State/Province', section: 'Address' },
      { name: 'country', label: 'Country', section: 'Address' },
      { name: 'account_id', label: 'Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Configuration', helperText: 'The default payment account for this location.' },
      {
        name: 'registers',
        label: 'Registers',
        type: 'subform',
        section: 'Registers',
        helperText: t('register.helperText'),
        subformFields: [
          { name: 'code', label: t('register.code'), readOnly: true, helperText: t('register.codeHelper') },
          { name: 'name', label: t('register.model'), required: true },
          { name: 'imei', label: t('register.serialNumber'), required: true },
          {
            name: 'device_type',
            label: t('register.deviceType'),
            type: 'select',
            defaultValue: 100,
            options: [
              { label: t('register.main'), value: 100 },
              { label: t('register.secondary'), value: 101 },
              { label: t('register.waiter'), value: 200 }
            ]
          },
          {
            name: 'deployment_category_ids',
            label: t('register.itemCategories'),
            type: 'multiselect',
            defaultValue: [],
            optionsEndpoint: 'all_categories/search',
            includeAllOption: true,
            helperText: t('register.itemCategoriesHelper')
          }
        ]
      }
    ],
    normalizeBeforeSave: (payload) => {
      const registers = Array.isArray(payload.registers) ? payload.registers : [];
      return {
        ...payload,
        // For each register:
        // - Preserve the id of existing registers (syncHasMany matches by id)
        // - Only blank the code for NEW registers (no id) so the backend
        //   auto-generates it via generate_device_no(imei)
        // - Coerce device_type to a number to match the smallInteger DB column
        registers: registers.map((r: Record<string, unknown>) => {
          const isNew = !r.id;
          const registerPayload = { ...r };
          delete registerPayload.deploymentCategories;
          delete registerPayload.deployment_categories;
          return {
            ...registerPayload,
            ...(isNew ? { code: '' } : {}), // only blank code for new registers
            device_type: r.device_type !== undefined && r.device_type !== '' ? Number(r.device_type) : 100,
            deployment_category_ids: deploymentCategoryIds(r.deployment_category_ids)
          };
        })
      };
    },
    normalizeAfterLoad: (record) => ({
      ...record,
      registers: Array.isArray(record.registers)
        ? record.registers.map((register) => {
            if (!register || typeof register !== 'object') return register;
            const row = register as Record<string, unknown>;
            const categories = row.deploymentCategories ?? row.deployment_categories;
            return {
              ...row,
              deployment_category_ids: relationIds(categories)
            };
          })
        : []
    })
  },
  {
    resource: 'price_groups',
    title: 'Price Groups',
    singular: 'Price Group',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'price-groups',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'details', label: 'Details' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'code', label: 'Code', helperText: 'Letters and numbers only, max 20 characters.' },
      { name: 'name', label: 'Name', required: true },
      { name: 'details', label: 'Details', type: 'textarea' }
    ]
  },
  {
    resource: 'customer_groups',
    title: 'Customer Groups',
    singular: 'Customer Group',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'customer-groups',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'discount', label: 'Discount' },
      { key: 'priceGroup', label: 'Price Group', render: (row) => relationName(row.priceGroup) }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'discount', label: 'Discount', type: 'number', required: true, defaultValue: 0 },
      { name: 'price_group_id', label: 'Price Group', type: 'select', optionsEndpoint: 'price_groups/search' }
    ]
  },
  {
    // Quotations: no listener fires on create (no QuotationEvent), so creates are
    // stock/journal/payment-neutral. Safe to write-enable without a draft guard.
    resource: 'quotations',
    title: 'Quotations',
    singular: 'Quotation',
    orderBy: 'date desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'customer', label: 'Customer', render: (row) => relationName(row.customer) },
      { key: 'status', label: 'Status' },
      { key: 'grand_total', label: 'Grand Total', render: (row) => moneyValue(row.grand_total) }
    ],
    detailSections: orderDetailSections('customer'),
    fields: [
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'customer_id', label: 'Customer', type: 'select', required: true, optionsEndpoint: 'customers/search', section: 'Details' },
      {
        name: 'items',
        label: 'Line Items',
        type: 'subform',
        section: 'Items',
        subformFields: [
          { name: 'item_id', label: 'Item ID', required: true },
          { name: 'code', label: 'Code' },
          { name: 'name', label: 'Name' },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 },
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 }
        ]
      }
    ],
    normalizeBeforeSave: normalizeQuotationBeforeSave
  },
  {
    // Draft return orders — now draft-gated (ReturnOrderEventListener skips
    // stock/journal/loyalty when draft=1, mirroring Sale/Purchase).
    resource: 'return_orders',
    title: 'Return Orders',
    singular: 'Return Order',
    orderBy: 'date desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'return-orders',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'customer', label: 'Customer', render: (row) => relationName(row.customer) },
      { key: 'supplier', label: 'Supplier', render: (row) => relationName(row.supplier) },
      { key: 'grand_total', label: 'Grand Total', render: (row) => moneyValue(row.grand_total) }
    ],
    detailSections: orderDetailSections('customer'),
    fields: [
      { name: 'type', label: 'Type', type: 'select', required: true, defaultValue: 'sale', options: ['sale', 'purchase'].map((value) => ({ label: value, value })), section: 'Details' },
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'customer_id', label: 'Customer', type: 'select', required: true, optionsEndpoint: 'customers/search', section: 'Details' },
      {
        name: 'items',
        label: 'Returned Items',
        type: 'subform',
        section: 'Items',
        helperText: t('order.noStockAffectedHelper'),
        subformFields: [
          { name: 'item_id', label: 'Item ID', required: true },
          { name: 'code', label: 'Code' },
          { name: 'name', label: 'Name' },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 },
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 }
        ]
      }
    ],
    normalizeBeforeSave: normalizeSaleBeforeSave
  },
  {
    resource: 'deliveries',
    title: 'Deliveries',
    singular: 'Delivery',
    orderBy: 'created_at desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    // Note: standalone "Add Delivery" is not possible — the backend store
    // route is POST /sales/{sale}/deliveries (requires a parent sale).
    // Edit is enabled (update status, assign driver, mark delivered).
    disableCreate: true,
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'status', label: 'Status' },
      { key: 'user', label: 'User', render: (row) => relationName(row.user) },
      { key: 'items', label: 'Items', render: (row) => countSummary(row, 'items', 'item') }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'date', label: 'Date', type: 'date', required: true, section: 'Details' },
      { name: 'reference', label: 'Reference', section: 'Details' },
      { name: 'status', label: 'Status', type: 'select', required: true, defaultValue: 'pending', options: ['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((value) => ({ label: value, value })), section: 'Status' },
      { name: 'driver', label: 'Driver', section: 'Status' },
      { name: 'delivered_at', label: 'Delivered At', type: 'date', section: 'Delivery', helperText: 'Required when status is "delivered".' },
      { name: 'delivered_by', label: 'Delivered By', section: 'Delivery' },
      { name: 'received_by', label: 'Received By', section: 'Delivery' },
      { name: 'details', label: 'Notes', type: 'textarea', section: 'Notes' }
    ]
  },
  {
    // RecurringSales: no listener fires on create, and the recurring:sales cron
    // skips drafts (scopeActive). normalizeRecurringSaleBeforeSave forces draft:1
    // so the template never auto-generates a real Sale until finalized in legacy.
    resource: 'recurring_sales',
    title: 'Recurring Sales',
    singular: 'Recurring Sale',
    orderBy: 'start_date desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'recurring-sales',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'start_date', label: 'Start' },
      { key: 'reference', label: 'Reference' },
      { key: 'customer', label: 'Customer', render: (row) => relationName(row.customer) },
      { key: 'repeat', label: 'Repeat' },
      { key: 'grand_total', label: 'Grand Total', render: (row) => moneyValue(row.grand_total) }
    ],
    detailSections: orderDetailSections('customer'),
    fields: [
      { name: 'customer_id', label: 'Customer', type: 'select', required: true, optionsEndpoint: 'customers/search', section: 'Details' },
      {
        name: 'repeat',
        label: 'Repeat',
        type: 'select',
        required: true,
        defaultValue: 'monthly',
        options: ['daily', 'weekly', 'monthly', 'quarterly', 'semiannually', 'yearly', 'biennially'].map((value) => ({ label: value, value })),
        section: 'Schedule'
      },
      { name: 'start_date', label: 'Start Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Schedule' },
      { name: 'date', label: 'Date', type: 'text', defaultValue: new Date().toISOString().slice(0, 10), section: 'Schedule' },
      {
        name: 'items',
        label: 'Line Items',
        type: 'subform',
        section: 'Items',
        helperText: t('order.draftTemplateHelper'),
        subformFields: [
          { name: 'item_id', label: 'Item ID', required: true },
          { name: 'code', label: 'Code' },
          { name: 'name', label: 'Name' },
          { name: 'price', label: 'Price', type: 'number', defaultValue: 0 },
          { name: 'quantity', label: 'Quantity', type: 'number', required: true, defaultValue: 1 }
        ]
      }
    ],
    normalizeBeforeSave: normalizeRecurringSaleBeforeSave
  },
  {
    resource: 'gift_cards',
    title: 'Gift Cards',
    singular: 'Gift Card',
    orderBy: 'created_at desc',
    searchField: 'number',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'gift-cards',
    columns: [
      { key: 'number', label: 'Number' },
      { key: 'customer', label: 'Customer', render: (row) => relationName(row.customer) },
      { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) },
      { key: 'balance', label: 'Balance', render: (row) => moneyValue(row.balance) },
      { key: 'expiry_date', label: 'Expiry' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'number', label: 'Card Number', required: true, section: 'Identity', helperText: 'Unique gift card number or code.' },
      { name: 'amount', label: 'Face Value', type: 'number', required: true, defaultValue: 0, section: 'Value', helperText: 'The original value loaded onto the card. The balance starts equal to this amount.' },
      { name: 'customer_id', label: 'Customer (optional)', type: 'select', optionsEndpoint: 'customers/search', section: 'Link' },
      { name: 'expiry_date', label: 'Expiry Date', type: 'date', section: 'Validity', helperText: 'Leave empty for no expiry.' },
      { name: 'details', label: 'Notes', type: 'textarea', section: 'Notes' }
    ]
  },
  {
    resource: 'promos',
    title: 'Promotions',
    singular: 'Promotion',
    orderBy: 'created_at desc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'code', label: 'Code' },
      { key: 'type', label: 'Type' },
      { key: 'start_date', label: 'Start' },
      { key: 'end_date', label: 'End' },
      { key: 'discount', label: 'Discount' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'type', label: 'Type', type: 'select', required: true, defaultValue: 'simple', options: ['simple', 'advance', 'BXGY', 'SXGD'].map((value) => ({ label: value, value })), section: 'Identity' },
      { name: 'code', label: 'Coupon Code', section: 'Identity', helperText: 'Optional coupon code customers enter at checkout.' },
      { name: 'discount', label: 'Discount', type: 'number', defaultValue: 0, section: 'Discount' },
      {
        name: 'discount_method',
        label: 'Discount Method',
        type: 'select',
        defaultValue: 'percentage',
        options: ['fixed', 'percentage'].map((value) => ({ label: value, value })),
        section: 'Discount'
      },
      {
        name: 'categories',
        label: 'Categories',
        type: 'multiselect',
        optionsEndpoint: 'all_categories/search',
        section: 'Targeting',
        helperText: 'Apply this promotion to items in the selected categories.'
      },
      { name: 'start_date', label: 'Start Date', section: 'Schedule' },
      { name: 'end_date', label: 'End Date', section: 'Schedule' },
      { name: 'active', label: 'Active', type: 'checkbox', defaultValue: true, section: 'Visibility' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ],
    normalizeAfterLoad: normalizePromotionAfterLoad
  },
  {
    // Payment Gateways: no observer, plain CRUD, no side effects. Was blocked
    // (list + delete 500s); both backend issues resolved — now full CRUD.
    resource: 'payment_gateways',
    title: 'Payment Gateways',
    singular: 'Payment Gateway',
    orderBy: 'created_at desc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'payment-gateways',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'url', label: 'URL', render: (row) => String(row.url ?? '-') },
      { key: 'active', label: 'Active', render: (row) => yesNoValue(row.active) },
      { key: 'description', label: 'Description' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'url', label: 'URL', required: true, section: 'Identity', helperText: 'The gateway endpoint URL.' },
      { name: 'form', label: 'Form', required: true, section: 'Identity', helperText: 'Gateway form template / HTML.' },
      { name: 'active', label: 'Active', type: 'checkbox', defaultValue: false, section: 'Status' },
      { name: 'merchant_no', label: 'Merchant No', section: 'Credentials' },
      { name: 'api_key', label: 'API Key', section: 'Credentials' },
      { name: 'auth_url', label: 'Auth URL', section: 'Endpoints' },
      { name: 'cancellation_url', label: 'Cancellation URL', section: 'Endpoints' },
      { name: 'description', label: 'Description', type: 'textarea', section: 'Notes' }
    ]
  },
  {
    resource: 'brands',
    title: 'Brands',
    singular: 'Brand',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'slug', label: 'Slug' },
      { key: 'order', label: 'Order' },
      { key: 'details', label: 'Details' }
    ],
    fields: [
      { name: 'code', label: 'Code', required: false },
      { name: 'name', label: 'Name', required: true },
      { name: 'order', label: 'Order', type: 'number' },
      { name: 'details', label: 'Details', type: 'textarea' }
    ]
  },
  {
    resource: 'units',
    title: 'Units',
    singular: 'Unit',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'base_unit', label: 'Base Unit', render: (row) => String((row.base_unit as { name?: string } | undefined)?.name ?? '-') },
      { key: 'operator', label: 'Operator' },
      { key: 'operation_value', label: 'Value' }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'base_id', label: 'Base Unit', type: 'select', optionsEndpoint: 'units/search' },
      {
        name: 'operator',
        label: 'Operator',
        type: 'select',
        options: [
          { label: '+', value: '+' },
          { label: '-', value: '-' },
          { label: '*', value: '*' },
          { label: '/', value: '/' }
        ]
      },
      { name: 'operation_value', label: 'Operation Value', type: 'number' },
      { name: 'details', label: 'Details', type: 'textarea' }
    ]
  },
  {
    resource: 'categories',
    title: 'Categories',
    singular: 'Category',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'parent', label: 'Parent', render: (row) => String((row.parent as { name?: string } | undefined)?.name ?? '-') },
      { key: 'color', label: 'Color', render: (row) => { const c = String(row.color ?? ''); return c ? `${c}` : '-'; } },
      { key: 'position', label: 'Position' }
    ],
    fields: [
      { name: 'code', label: 'Code', readOnly: true, helperText: 'Generated automatically when you save.' },
      { name: 'name', label: 'Name', required: true },
      { name: 'parent_id', label: 'Parent Category', type: 'select', optionsEndpoint: 'all_categories/search' },
      { name: 'color', label: 'Color', type: 'color' },
      { name: 'position', label: 'Position', type: 'number', defaultValue: 99 }
    ]
  },
  {
    resource: 'taxes',
    title: 'Taxes',
    singular: 'Tax',
    orderBy: 'code asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'rate', label: 'Rate' },
      { key: 'number', label: 'Tax Number' },
      { key: 'compound', label: 'Compound', render: (row) => (row.compound ? 'Yes' : 'No') }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'name', label: 'Name', required: true },
      { name: 'rate', label: 'Rate', type: 'number' },
      { name: 'number', label: 'Tax Number' },
      { name: 'details', label: 'Details', type: 'textarea' },
      { name: 'tax_in_item_price', label: 'Tax in item price', type: 'checkbox' },
      { name: 'compound', label: 'Compound tax', type: 'checkbox' },
      { name: 'recoverable', label: 'Recoverable tax', type: 'checkbox' },
      { name: 'state', label: 'State tax', type: 'checkbox' },
      { name: 'same', label: 'Same state', type: 'select', options: yesNo }
    ],
    normalizeBeforeSave: (payload) => boolFlags(payload, ['tax_in_item_price', 'compound', 'recoverable', 'state', 'same'])
  },
  {
    resource: 'fields',
    title: 'Fields',
    singular: 'Field',
    orderBy: 'order asc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'slug', label: 'Slug' },
      { key: 'name', label: 'Name' },
      { key: 'type', label: 'Type' },
      { key: 'entities', label: 'Entities', render: (row) => (Array.isArray(row.entities) ? row.entities.join(', ') : String(row.entities ?? '-')) },
      { key: 'required', label: 'Required', render: (row) => (row.required ? 'Yes' : 'No') }
    ],
    fields: [
      { name: 'name', label: 'Name', required: true },
      { name: 'slug', label: 'Slug', required: true },
      {
        name: 'type',
        label: 'Type',
        type: 'select',
        required: true,
        options: ['text', 'number', 'select', 'textarea', 'checkbox', 'radio', 'date', 'datetime'].map((value) => ({ label: value, value }))
      },
      {
        name: 'entities',
        label: 'Entities',
        type: 'multiselect',
        required: true,
        options: [
          'customer',
          'delivery',
          'expense',
          'income',
          'item',
          'location',
          'payment',
          'purchase',
          'quotation',
          'return_order',
          'sale',
          'stock_adjustment',
          'stock_transfer',
          'supplier'
        ].map((value) => ({ label: value, value }))
      },
      { name: 'options', label: 'Options', helperText: 'Separate options with | when type requires options.' },
      { name: 'order', label: 'Order', type: 'number' },
      { name: 'required', label: 'Required', type: 'checkbox' },
      { name: 'details', label: 'Details', type: 'textarea' }
    ],
    normalizeBeforeSave: (payload) => boolFlags(payload, ['required'])
  },
  {
    resource: 'halls',
    title: 'Halls',
    singular: 'Hall',
    orderBy: 'code asc',
    searchField: 'title',
    searchMode: 'local',
    localSearchLimit: 200,
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'title', label: 'Title' },
      { key: 'location', label: 'Location', render: (row) => String((row.location as { name?: string } | undefined)?.name ?? '-') },
      { key: 'details', label: 'Details' }
    ],
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'title', label: 'Title', required: true },
      { name: 'location_id', label: 'Location', type: 'select', required: true, optionsEndpoint: 'locations/search' },
      { name: 'details', label: 'Details', type: 'textarea' }
    ]
  },
  {
    // Standalone Modifier CRUD (audit-verified safe: no observer; touches only
    // modifiers, modifier_options, and the item_modifier pivot).
    resource: 'modifiers',
    title: 'Modifiers',
    singular: 'Modifier',
    orderBy: 'created_at desc',
    searchField: 'title',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'modifiers',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'title', label: 'Title' },
      { key: 'show_as', label: 'Show As' },
      { key: 'details', label: 'Details' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'code', label: 'Code', required: true, section: 'Identity' },
      { name: 'title', label: 'Title', required: true, section: 'Identity' },
      {
        name: 'show_as',
        label: 'Show As',
        type: 'select',
        defaultValue: 'checkbox',
        options: ['checkbox', 'radio', 'select'].map((value) => ({ label: value, value })),
        section: 'Identity'
      },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ]
  },
  {
    // Calendar event (audit-verified safe: no observer, no financial fields).
    resource: 'events',
    title: 'Events',
    singular: 'Event',
    orderBy: 'start_date desc',
    searchField: 'title',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'events',
    columns: [
      { key: 'title', label: 'Title' },
      { key: 'start_date', label: 'Start' },
      { key: 'end_date', label: 'End' },
      { key: 'color', label: 'Color', render: (row) => String(row.color ?? '-') }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'title', label: 'Title', required: true, section: 'Details' },
      { name: 'start_date', label: 'Start Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Schedule' },
      { name: 'end_date', label: 'End Date', type: 'text', section: 'Schedule' },
      { name: 'start_time', label: 'Start Time', type: 'text', section: 'Schedule' },
      { name: 'end_time', label: 'End Time', type: 'text', section: 'Schedule' },
      { name: 'color', label: 'Color', type: 'color', section: 'Details' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ]
  },
  {
    // Restaurant/hall table (audit-verified safe: no observer, no financial fields).
    resource: 'tables',
    title: 'Tables',
    singular: 'Table',
    orderBy: 'code asc',
    searchField: 'title',
    searchMode: 'local',
    localSearchLimit: 200,
    permissionResource: 'tables',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'title', label: 'Title' },
      { key: 'hall', label: 'Hall', render: (row) => relationName(row.hall) },
      { key: 'details', label: 'Details' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'code', label: 'Code', required: true },
      { name: 'title', label: 'Title', required: true },
      { name: 'hall_id', label: 'Hall', type: 'select', required: true, optionsEndpoint: 'halls/search' },
      { name: 'details', label: 'Details', type: 'textarea' }
    ]
  },
  {
    // Merchant / tenant profile (CONDITIONAL per audit: create/update only,
    // super-gated, destroy disabled — it's the business-config boundary).
    resource: 'merchants',
    title: 'Merchant',
    singular: 'Merchant',
    orderBy: 'created_at desc',
    searchField: 'name',
    searchMode: 'local',
    localSearchLimit: 50,
    disableDelete: true,
    permissionResource: 'merchants',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'city', label: 'City' },
      { key: 'currency', label: 'Currency' }
    ],
    detailSections: simpleDetailSections,
    fields: [
      { name: 'name', label: 'Name', required: true, section: 'Identity' },
      { name: 'email', label: 'Email', section: 'Identity' },
      { name: 'phone', label: 'Phone', section: 'Identity' },
      { name: 'merchant_nr', label: 'Merchant No', readOnly: true, section: 'Identity' },
      { name: 'type', label: 'Type', type: 'text', readOnly: true, section: 'Identity' },
      { name: 'address', label: 'Address', section: 'Location' },
      { name: 'street_no', label: 'Street No', section: 'Location' },
      { name: 'city', label: 'City', section: 'Location' },
      { name: 'state', label: 'State', section: 'Location' },
      { name: 'postal_code', label: 'Postal Code', section: 'Location' },
      { name: 'country', label: 'Country', section: 'Location' },
      { name: 'currency', label: 'Currency', readOnly: true, section: 'Config' },
      { name: 'timezone', label: 'Timezone', section: 'Config' },
      { name: 'locale', label: 'Locale', section: 'Config' },
      { name: 'tax_id', label: 'Tax ID', section: 'Config' },
      { name: 'ticket_footer_info', label: 'Ticket Footer Info', type: 'textarea', section: 'Tickets' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ]
  },
  {
    // Salaries: gated on status (due=safe, paid=posts journal). The
    // SalaryObserver debits the account journal when status flips to 'paid'.
    // normalizeBeforeSave forces status='due' so React never posts a journal.
    resource: 'salaries',
    title: 'Salaries',
    singular: 'Salary',
    orderBy: 'date desc',
    searchField: 'reference',
    searchMode: 'local',
    localSearchLimit: 200,
    canFinalize: true,
    draftField: 'status',
    draftValue: 'due',
    finalizeField: 'status',
    finalizeValue: 'paid',
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'reference', label: 'Reference' },
      { key: 'type', label: 'Type' },
      { key: 'user', label: 'Employee', render: (row) => relationName(row.user) },
      { key: 'account', label: 'Account', render: (row) => relationName(row.account) },
      { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) },
      { key: 'status', label: 'Status' }
    ],
    detailSections: [
      {
        title: 'Summary',
        fields: [
          { key: 'date', label: 'Date' },
          { key: 'type', label: 'Type' },
          { key: 'status', label: 'Status' },
          { key: 'reference', label: 'Reference' },
          { key: 'user', label: 'Employee', render: (row) => relationName(row.user) },
          { key: 'account', label: 'Account', render: (row) => relationName(row.account) }
        ]
      },
      {
        title: 'Amounts',
        fields: [
          { key: 'amount', label: 'Amount', render: (row) => moneyValue(row.amount) },
          { key: 'work_hours', label: 'Work Hours' },
          { key: 'work_hours_salary', label: 'Hourly Rate', render: (row) => moneyValue(row.work_hours_salary) },
          { key: 'points', label: 'Points' },
          { key: 'advance', label: 'Advance' }
        ]
      },
      { title: 'Notes', fields: [{ key: 'details', label: 'Details' }] }
    ],
    fields: [
      { name: 'date', label: 'Date', type: 'text', required: true, defaultValue: new Date().toISOString().slice(0, 10), section: 'Details' },
      { name: 'type', label: 'Type', type: 'select', required: true, defaultValue: 'salary', options: ['salary', 'commission'].map((value) => ({ label: value, value })), section: 'Details', helperText: 'Commission deducts loyalty/staff points from the employee.' },
      { name: 'user_id', label: 'Employee', type: 'select', required: true, optionsEndpoint: 'users/search', section: 'Details' },
      { name: 'account_id', label: 'Account', type: 'select', required: true, optionsEndpoint: 'accounts/search', section: 'Details' },
      { name: 'amount', label: 'Amount', type: 'number', required: true, defaultValue: 0, section: 'Details', helperText: 'Draft — not posted to the ledger until finalized (status → paid).' },
      { name: 'reference', label: 'Reference', section: 'Details' },
      { name: 'advance', label: 'Advance Payment', type: 'checkbox', section: 'Status' },
      { name: 'work_hours', label: 'Work Hours', type: 'number', section: 'Status' },
      { name: 'work_hours_salary', label: 'Hourly Rate', type: 'number', section: 'Status' },
      { name: 'points', label: 'Points', type: 'number', section: 'Status', helperText: 'Required for commission type. Deducted from the employee\'s points balance on finalize.' },
      { name: 'details', label: 'Details', type: 'textarea', section: 'Notes' }
    ],
    normalizeBeforeSave: (payload) => ({
      date: payload.date || new Date().toISOString().slice(0, 10),
      type: payload.type || 'salary',
      user_id: payload.user_id,
      account_id: payload.account_id,
      amount: Number(payload.amount ?? 0),
      reference: payload.reference ?? '',
      details: payload.details ?? '',
      advance: payload.advance ? 1 : 0,
      work_hours: payload.work_hours ? Number(payload.work_hours) : null,
      work_hours_salary: payload.work_hours_salary ? Number(payload.work_hours_salary) : null,
      points: payload.type === 'commission' && payload.points ? Number(payload.points) : null,
      // HARD GUARDRAIL: never post a journal entry from React. Finalize
      // (status→paid) is a deliberate action that triggers the journal posting.
      status: 'due'
    })
  }
];

// Domain override modules (crud/overrides/<domain>.ts) can patch or replace any
// base config, or add brand-new resources, without editing this file:
//   export const overrides: Record<string, (base: CrudResourceConfig | undefined) => CrudResourceConfig>
// Overrides are applied in file-name order.
type OverrideModule = { overrides?: Record<string, (base: CrudResourceConfig | undefined) => CrudResourceConfig> };
const overrideModules = import.meta.glob<OverrideModule>('./overrides/*.ts', { eager: true });

const resolvedConfigs: CrudResourceConfig[] = (() => {
  const byName = new Map<string, CrudResourceConfig>(referenceResources.map((config) => [config.resource, config]));
  Object.keys(overrideModules)
    .sort()
    .forEach((path) => {
      Object.entries(overrideModules[path].overrides ?? {}).forEach(([resource, patch]) => {
        byName.set(resource, { ...patch(byName.get(resource)), resource });
      });
    });
  return Array.from(byName.values());
})();

/** All resource configs, with domain overrides applied. */
export function allResourceConfigs() {
  return resolvedConfigs;
}

export function resourceConfigByName(resource: string) {
  return resolvedConfigs.find((config) => config.resource === resource);
}

// Resources that are intentionally excluded from active React navigation.
// Each entry must explain why it is blocked so the health page can surface it.
export type BlockedResource = {
  resource: string;
  title: string;
  reason: string;
};

// Resources that are intentionally excluded from active React navigation.
// Each entry must explain why it is blocked so the health page can surface it.
// (Currently empty — payment_gateways was unblocked after the backend 500 was
// resolved and is now a normal write-enabled resource.)
export const blockedResources: BlockedResource[] = [];
